import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useContentContext, useWarehouseContext, useAuthContext, } from "../../../app/providers/contextIndex";
import PageSectionRenderer from "../components/PageSectionRenderer";
import { Carousel } from "primereact/carousel";
import { DataView } from "primereact/dataview";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ensureCatalogAuth, getAffariListaPrecioBaseRaw, getAffariCanal, } from "../../../services/backend/authService";
import { getBanners, getCategorias, getDestacados, } from "../../../services/backend/contentBackendService";
import { getCompany } from "../../../config/companyConfig";
import { carouselPt } from "../utils/carouselConfig";
import { sanitizeHtml, sanitizeRelativeUrl } from "../utils/htmlSanitize";
import "../../catalog/styles/catalog.css";
import { getTopProductDiscountedPrice, getCatalogRegularPrice, resolveProductImage } from "../../catalog/utils/catalogUtils";
import { buildProductDetailPath } from "../../../shared/utils/topbarUtils";
import { FALLBACK_IMAGE } from "../../../utils/uiConstants";
import logger from "../../../shared/utils/logger";
import { Image } from "primereact/image";

const Home = () => {
  const { activeLandingId } = useOutletContext() || {};
  const { getPagesByLanding } = useContentContext();
  const { selectedAlmacen } = useWarehouseContext();
  const { isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [destacados, setDestacados] = useState([]);
  const canal = getAffariCanal();
  const categoriasConfig = getCompany().categorias?.[canal] || {};

  const loadProtectedResource = async (isStillCurrent, fetcher, errorLabel) => {
    try {
      await ensureCatalogAuth();
      const res = await fetcher();
      if (!isStillCurrent()) return null;
      return res;
    } catch (error) {
      logger.error(errorLabel, error);
      return null;
    }
  };

  useEffect(() => {
    let isCurrent = true;
    loadProtectedResource(
      () => isCurrent,
      () => getBanners({ canal }),
      "No fue posible cargar los banners."
    ).then((res) => {
      if (res && !res.error && Array.isArray(res.banners)) setBanners(res.banners);
    });
    return () => { isCurrent = false; };
  }, [canal]);

  useEffect(() => {
    let isCurrent = true;
    loadProtectedResource(
      () => isCurrent,
      () => getCategorias({
        limiteCategorias: categoriasConfig.limite || "",
        tipoCategorias: categoriasConfig.tipo || "",
        idsFamilias: categoriasConfig.ids || "",
      }),
      "No fue posible cargar las categorías."
    ).then((res) => {
      if (res && !res.error && Array.isArray(res.categorias)) setCategorias(res.categorias);
    });
    return () => { isCurrent = false; };
  }, [canal, isAuthenticated, categoriasConfig.limite, categoriasConfig.tipo, categoriasConfig.ids]);

  useEffect(() => {
    if (!selectedAlmacen) return;
    let isCurrent = true;
    loadProtectedResource(
      () => isCurrent,
      () => {
        const COMPANY = getCompany();
        const destacadosCanalCfg = COMPANY.destacados?.[canal] || {};
        const tipoDestacados = destacadosCanalCfg.tipo || "";
        const limiteDestacados = destacadosCanalCfg.limite ?? "";
        const codigos = destacadosCanalCfg.codigos || "";
        return getDestacados({
          almacen: isAuthenticated ? selectedAlmacen : "",
          listaPrecio: isAuthenticated ? getAffariListaPrecioBaseRaw() : "",
          tipo: canal,
          tipoDestacados,
          limiteDestacados,
          codigos,
        });
      },
      "No fue posible cargar los destacados."
    ).then((res) => {
      if (res && !res.error && Array.isArray(res.productos)) setDestacados(res.productos);
    });
    return () => { isCurrent = false; };
  }, [selectedAlmacen, isAuthenticated, canal]);

  const sections = useMemo(() => {
    const pages = getPagesByLanding(activeLandingId);
    const homePages = pages.filter((page) => page.isHome);
    return homePages.reduce((acc, page) => {
      if (Array.isArray(page.sections)) {
        acc.push(...page.sections);
      }
      return acc;
    }, []);
  }, [activeLandingId, getPagesByLanding]);

  const buildDestacadoState = (item) => ({
    productCode: String(item?.codigo || "").trim(),
    productName: item?.descripcion || "",
    productPrice: Number(item?.precio || 0),
  });

  const productCardTemplate = (item) => (
    <article className="catalog-card" style={{ cursor: "pointer" }}>
      <div
        className="catalog-card-media"
        onClick={() => item.url && navigate("/catalogo/detalle" + buildProductDetailPath(item.descripcion || item.url || ""), { state: buildDestacadoState(item) })}
      >
        <Image
          src={ resolveProductImage(item.foto, item.mime) }
          alt={item.descripcion}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="catalog-card-body">
        <h3>{item.descripcion}</h3>

        <div className={`catalog-card-price-row ${item.descuento > 0 ? "has-discount" : ""}`}>
          <span className="catalog-card-price">
            {getTopProductDiscountedPrice(item.precio, item.descuento)}
          </span>
          {item.descuento > 0 ? (
            <Tag
              value={`-${item.descuento}%`}
              severity="success"
              className="catalog-card-discount-tag"
            />
          ) : null}
        </div>
        {getCatalogRegularPrice(item.precio, item.descuento) ? (
          <span className="catalog-card-regular-price p-text-line-through">
            Normal {getCatalogRegularPrice(item.precio, item.descuento)}
          </span>
        ) : null}
      </div>
      <div className="catalog-card-footer">
        <Button
          label="Ver Producto"
          className="catalog-card-cta p-button-sm"
          onClick={() => item.url && navigate("/catalogo/detalle" + buildProductDetailPath(item.descripcion || item.url || ""), { state: buildDestacadoState(item) })}
        />
      </div>
    </article>
  );

   const categoryCardTemplate = (item) => (
    <article className="catalog-card" style={{ cursor: "pointer" }}>
      <div className="catalog-card-media" onClick={() => item.url && navigate(`/categoria/${item.url}`)}>
        <Image
          src={ resolveProductImage(item.foto, item.mime) }
          alt={item.descripcion}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="catalog-card-body">
        <h3>{item.descripcion}</h3>
      </div>
      <div className="catalog-card-footer">
        <Button
          label="Ver Productos"
          className="catalog-card-cta p-button-sm"
          onClick={() => item.url && navigate(`/categoria/${item.url}`)}
        />
      </div>
    </article>
  );

  const bannerTemplate = (banner) => (
    <div
      className="banner-slide"
      style={{ backgroundImage: `url(${resolveProductImage(banner.foto, banner.mime)})`, }}
      onClick={() => {
        const safe = sanitizeRelativeUrl(banner.url);
        if (safe) {
          if (banner.clase === 1) {
            navigate(`/categoria/${safe}`);
          } else if (banner.clase === 2) {
            navigate(`/catalogo/detalle/${safe}`);
          }
        }
      }}
      onTransitionEnd={(e) => e.stopPropagation()}
    >
      <div className="banner-overlay">
        <div className="ml-7">
          {banner.titulo && (
            <div
              className="banner-titulo"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(banner.titulo) }}
            />
          )}
          {banner.subtitulo && (
            <div
              className="banner-subtitulo"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(banner.subtitulo) }}
            />
          )}
          {banner.texto && (
            <div
              className="banner-texto"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(banner.texto) }}
            />
          )}
          {banner.boton && (
            <Button className="mt-3" rounded label={banner.boton} />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="landing-container dynamic-page">
      {banners.length > 0 && (
        <section>
          {sections.filter((s) => s?.type === "franja").map((section, index) => (
              <PageSectionRenderer
                key={`${section?.type || "section"}-${index}`}
                section={section}
                index={index}
              />
          ))}
          <Carousel
            value={banners}
            itemTemplate={bannerTemplate}
            numVisible={1}
            numScroll={1}
            circular
            autoplayInterval={5000}
            showIndicators
            showNavigators
            pt={carouselPt}
          />
        </section>
      )}

      {categorias.length > 0 && (
        <section className="dynamic-section">
          <h2>{categoriasConfig.title || "Categorías"}</h2>
          {categoriasConfig.subtitle && (
            <p className="dynamic-subtitle">{categoriasConfig.subtitle}</p>
          )}
          <DataView
            value={categorias}
            itemTemplate={categoryCardTemplate}
            layout="grid"
            className="catalog-dataview-grid"
          />
        </section>
      )}

      {destacados.length > 0 && (
        <section className="dynamic-section">
          <h2>Productos Destacados</h2>
          <p className="dynamic-subtitle">
            Calidad y rendimiento que marcan la diferencia
          </p>
          <DataView
            value={destacados}
            itemTemplate={productCardTemplate}
            layout="grid"
            className="catalog-dataview-grid"
          />
        </section>
      )}

      {sections
        .filter((s) => s?.type !== "franja")
        .map((section, index) => (
          <PageSectionRenderer
            key={`${section?.type || "section"}-${index}`}
            section={section}
            index={index}
          />
        ))}
    </div>
  );
};

export default Home;
